import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Grid, useTheme } from '@mui/material'
import { ColStack } from 'components/atoms/container/InCardStack'
import { FloatingGrid } from 'components/atoms/container/ResponsiveContainers'
import {
  TitleGroup,
  getOnboardingSteps,
  OnboardingStepCompletePopup
} from 'components/molecules/onboarding/PathOnboardingMolecules'
import StepOne from 'components/organisms/user/onboardingSteps/new/StepOne'
import StepTwo from 'components/organisms/user/onboardingSteps/new/StepTwo'
import Stepper from 'components/molecules/onboarding/Stepper'
import FixedLeftPanelPageTemplate from 'components/templates/FixedLeftPanelPageTemplate'
import { getGoalInfoById } from 'utils/getSupportingInfo'
import { getLocalUser, getUpdateLocalUser } from 'utils/localStorage'
import { updateStates, getUserSurveys } from 'services/userServices'
import OverlayLoading from 'components/atoms/OverlayLoading'
import RequestRetryAlert from 'components/atoms/RequestRetryAlert'
import SelfAssessmentCard from 'components/organisms/assessments/SelfAssessmentCard'
import SelfAssessmentReport from 'components/molecules/assessments/SelfAssessmentReport'
import UserSurveyCard from 'components/organisms/assessments/UserSurveyCard'
import { MY_JOURNEY } from 'routes/constants'
import { USER_SURVEY_STATUSES, FEATURE_KEYS } from 'services/constants'
import { isAllowedFeature } from 'utils/permissionCheck'

const OnboardingCard = () => {
  const theme = useTheme()
  const location = useLocation()
  const navigate = useNavigate()

  const [selectedGoal, setSelectedGoal] = useState({})
  const [requestError, setRequestError] = useState(undefined)
  const [loading, setLoading] = useState(false)
  const [assessmentResultData, setAssessmentResultData] = useState({})
  const [openStepCompleteDialog, setOpenStepCompleteDialog] = useState(false)
  const [skippedFeedback, setSkippedFeedback] = useState(false)
  const userInfo = getLocalUser()
  const onboardingSteps = getOnboardingSteps()

  // Set initial active step
  const forProfile = location.state?.completeProfile === true
  const profileStepCompleted =
    userInfo.onboardingAnswers &&
    userInfo.onboardingAnswers[0] !== undefined &&
    userInfo.onboardingAnswers[1] !== undefined

  const personalizedStepCompleted = Boolean(
    userInfo.onboardingAnswers &&
      userInfo.onboardingAnswers['skills']?.length > 0
  )

  const initialStep = forProfile
    ? 0
    : profileStepCompleted || personalizedStepCompleted
      ? 1
      : 0
  const initialSubStep = forProfile ? 0 : personalizedStepCompleted ? 1 : 0
  const [step, setStep] = useState(initialStep)
  const [subStep, setSubStep] = useState(initialSubStep)

  useEffect(() => {
    const fetchData = async () => {
      const user = await getUpdateLocalUser()
      const userSurveys = await getUserSurveys()
      const pendingSurvey = userSurveys?.find(
        s => s.status === USER_SURVEY_STATUSES.PENDING
      )

      // If onboarding self assessment is already completed
      if (user?.onboardingSelfAssessmentComplete) {
        if (pendingSurvey && userSurveys?.length === 1) {
          setStep(4)
          setSubStep(0)
        } else {
          navigate(MY_JOURNEY)
        }
        return
      }

      // Set user goal if exists
      if (user?.userStates?.currentGoalId) {
        setLoading(true)
        const goal = await getGoalInfoById(user.userStates.currentGoalId)
        // Skip to Step 3 if goal is already set
        if (goal) {
          setSelectedGoal(goal)
          if (
            !forProfile &&
            profileStepCompleted &&
            personalizedStepCompleted
          ) {
            setStep(2)
            setSubStep(0)
          }
        }
        setLoading(false)
      }
    }
    if (isAllowedFeature(FEATURE_KEYS.GOAL_CONTENT)) {
      fetchData()
    } else {
      navigate(MY_JOURNEY)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateCurrentPath = async goal => {
    setLoading(true)
    const res = await updateStates(
      {
        currentGoalStepId: '',
        currentGoalId: goal.id
      },
      true
    )
    if (res.status === 200) {
      setSelectedGoal(goal)
      // Move to next step
      onNext()
    } else {
      setRequestError(true)
    }
    setLoading(false)
  }

  const onNext = () => {
    if (
      onboardingSteps[step]?.maxSubSteps &&
      onboardingSteps[step].maxSubSteps - 1 > subStep
    ) {
      setSubStep(subStep + 1)
    } else if (step < onboardingSteps.length - 1) {
      setStep(step + 1)
      setSubStep(0)
    } else {
      navigate(MY_JOURNEY)
    }
  }

  const onBack = () => {
    if (subStep > 0) {
      setSubStep(subStep - 1)
    } else if (step > 0) {
      setStep(step - 1)
      const previousStep = onboardingSteps[step - 1]?.maxSubSteps || 0
      setSubStep(previousStep > 0 ? previousStep - 1 : previousStep)
    }
  }

  return (
    <>
      <OverlayLoading open={loading} />
      <FixedLeftPanelPageTemplate
        leftPanelChildren={<TitleGroup step={step} goal={selectedGoal} />}
        rightPanelHeader={
          <Stepper step={step} onboardingSteps={onboardingSteps} />
        }
        leftPanelBackground={onboardingSteps[step]?.backgroundColor}
        isLightText={onboardingSteps[step]?.isLightText}
        containerProps={{
          'data-testid': 'all-page-content'
        }}>
        <FloatingGrid
          width='100%'
          backgroundColor={theme.palette.background.paper}
          container
          direction='column'
          wrap='nowrap'>
          <Grid width='100%' flexGrow={1}>
            <ColStack spacing={16} height='inherit'>
              {step === 0 ? (
                <StepOne
                  subStep={subStep}
                  onNextCallback={onNext}
                  onBackCallback={onBack}
                />
              ) : step === 1 ? (
                <StepTwo
                  subStep={subStep}
                  setSelectedGoal={updateCurrentPath}
                  onNextCallback={onNext}
                  onBackCallback={onBack}
                />
              ) : step === 2 ? (
                <SelfAssessmentCard
                  goalId={selectedGoal.id}
                  nextStepCallback={() => setOpenStepCompleteDialog(true)}
                  previousStepCallback={onBack}
                  fromOnboarding={true}
                  setResultData={setAssessmentResultData}
                />
              ) : step === 3 ? (
                <SelfAssessmentReport
                  resultData={assessmentResultData}
                  disableBehaviorClick={true}
                  fromOnboarding={true}
                  nextStepCallback={skipFeedback => {
                    setOpenStepCompleteDialog(true)
                    setSkippedFeedback(skipFeedback)
                  }}
                />
              ) : step === 4 ? (
                <UserSurveyCard
                  fromOnboarding={true}
                  submittedCallback={() => {
                    setOpenStepCompleteDialog(true)
                    setSkippedFeedback(false)
                  }}
                />
              ) : null}

              {requestError && <RequestRetryAlert />}
            </ColStack>
          </Grid>
        </FloatingGrid>
      </FixedLeftPanelPageTemplate>

      {openStepCompleteDialog && (
        <OnboardingStepCompletePopup
          open={openStepCompleteDialog}
          handleContinue={() => {
            setOpenStepCompleteDialog(false)
            onNext()
          }}
          fromOnboarding={true}
          currentOnboardingStep={step}
          skippedFeedback={skippedFeedback}
          handleBack={() => {
            setOpenStepCompleteDialog(false)
          }}
        />
      )}
    </>
  )
}

export default OnboardingCard
